/**
 * The one number input. Replaces <input type="number"> and hand-formatted money boxes, which fought the person
 * typing: an emptied box snapped back to "0" (so "43" became "043"), the thousands separator was re-inserted on
 * every key (the caret jumped to the end), the mouse wheel changed values while scrolling, and fields that
 * recompute the plan saved half-typed numbers.
 *
 * While focused the box shows exactly what was typed (no reformatting, no clamping); leading zeros are dropped
 * and it can be emptied. It shows "125 000" style grouping when not focused, clamps to min/max when you leave,
 * steps with ↑/↓, and never reacts to the wheel. `commit="blur"` reports the number only when you leave the box
 * or press Enter — for fields whose change is expensive (a total or target that rebuilds the plan).
 */
import { useEffect, useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react';

type Native = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'min' | 'max' | 'step' | 'defaultValue'>;
export interface NumberFieldProps extends Native {
  value: number | null | undefined;
  /** The number typed; null when the box is empty (only when `allowEmpty`). */
  onChange: (n: number | null) => void;
  min?: number;
  max?: number;
  /** ↑/↓ step (and the decimals shown follow it: 0.05 → 2 decimals). */
  step?: number;
  /** Decimals kept; default from `step` (0 for whole numbers). */
  decimals?: number;
  /** Group thousands when not editing ("125 000"). Default true for whole numbers ≥ 10 000. */
  group?: boolean;
  /** Text after the number inside the box, e.g. "%" or "SEK". */
  suffix?: string;
  /** 'change' (default): every valid keystroke; 'blur': on leaving the box or Enter. */
  commit?: 'change' | 'blur';
  /** An empty box means "no value" (null) instead of reverting to the last number. */
  allowEmpty?: boolean;
  /** Classes for the wrapper (the box look); `className` goes to the <input>. */
  boxClassName?: string;
}

const decimalsOf = (step?: number) => (step && step < 1 ? Math.min(4, (String(step).split('.')[1] || '').length) : 0);
const round = (n: number, d: number) => (d ? Math.round(n * 10 ** d) / 10 ** d : Math.round(n));

export function formatNumber(n: number, decimals = 0, group = true): string {
  const s = decimals ? n.toFixed(decimals).replace(/\.?0+$/, '') : String(Math.round(n));
  if (!group) return s;
  const [int, frac] = s.split('.');
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')}${frac ? `.${frac}` : ''}`;
}
/**
 * What was typed → a number; null when empty or unreadable. Spaces are grouping ("220 000"). A comma or dot is the
 * decimal mark ("15,5"), except dot/comma groups of three digits, which are thousands ("1.250.000"; "220.000" in a
 * whole-number field). With `decimals` 0 the result is rounded — a typed "15,5" in a whole-number field is 16, never 155.
 */
export function parseNumber(raw: string, decimals?: number): number | null {
  let t = raw.replace(/[\s\u00a0\u202f]/g, '').replace(/[^\d.,-]/g, '');
  if (t.lastIndexOf('-') > 0) return null;                       // "10-20" is a range, not 1020
  const neg = t.startsWith('-'); t = t.replace(/-/g, '');
  if (!t || /^[.,]+$/.test(t)) return null;
  const seps = (t.match(/[.,]/g) || []).length;
  let n: number;
  if (!seps) n = Number(t);
  else if (/^\d{1,3}([.,]\d{3})+$/.test(t) && (seps > 1 || decimals === 0)) n = Number(t.replace(/[.,]/g, ''));
  else { const i = Math.max(t.lastIndexOf('.'), t.lastIndexOf(',')); n = Number(`${t.slice(0, i).replace(/[.,]/g, '') || '0'}.${t.slice(i + 1) || '0'}`); }
  if (!Number.isFinite(n)) return null;
  if (neg) n = -n;
  return decimals === 0 ? Math.round(n) : n;
}
/**
 * The box shows what is being typed: digits, spaces, decimal/thousands marks and (when negatives are allowed) a
 * leading minus; letters and currency are dropped and leading zeros too ("043" → "43", "0,5" stays). Nothing jumps
 * while typing — parseNumber reads it, and the box is formatted when you leave it.
 */
export function cleanTyped(raw: string, opts: { decimals: number; negative: boolean }): string {
  let t = raw.replace(/[^\d.,\s\u00a0\u202f-]/g, '');
  const neg = opts.negative && t.trimStart().startsWith('-');
  // A leading minus only where negatives are allowed; a minus inside stays, so a pasted range ("10-20") reads as no number.
  t = t.replace(/^[\s\u00a0\u202f]*-+/, '').replace(/^[\s\u00a0\u202f]+/, '').replace(/[\s\u00a0\u202f]{2,}/g, ' ');
  // A decimal field with one mark keeps at most `decimals` digits after it ("2,55" → "2,5" in a 1-decimal field).
  const marks = t.match(/[.,]/g) || [];
  if (opts.decimals && marks.length === 1) { const i = t.search(/[.,]/); t = `${t.slice(0, i + 1)}${t.slice(i + 1).replace(/\D/g, '').slice(0, opts.decimals)}`; }
  t = t.replace(/^0+(?=\d)/, '');
  return `${neg ? '-' : ''}${t}`.slice(0, 24);
}

export function NumberField({ value, onChange, min, max, step = 1, decimals, group, suffix, commit = 'change', allowEmpty = false, boxClassName, className, onFocus, onBlur, onKeyDown, placeholder, disabled, readOnly, ...rest }: NumberFieldProps) {
  const dec = decimals ?? decimalsOf(step);
  const grouped = (n: number) => group ?? (!dec && Math.abs(n) >= 10000);
  const shown = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? '' : formatNumber(n, dec, grouped(n)));
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const last = useRef<number | null>(value ?? null);
  useEffect(() => { if (!focused) last.current = value ?? null; }, [value, focused]);

  // The box being edited shows the number as it looked (grouped with plain spaces), so focusing never makes it jump.
  const editText = (n: number) => formatNumber(n, dec, grouped(n)).replace(/\u00a0/g, ' ');
  const clamp = (n: number) => round(Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n)), dec);
  const report = (n: number | null) => { if (n !== (value ?? null)) onChange(n); };
  const finish = () => {
    const n = parseNumber(draft, dec);
    if (n == null) { if (allowEmpty) report(null); else if (commit === 'blur') { /* nothing typed: keep the value */ } else report(last.current); }
    else report(clamp(n));
  };
  const typed = (raw: string) => {
    const t = cleanTyped(raw, { decimals: dec, negative: (min ?? 0) < 0 });
    setDraft(t);
    if (commit !== 'change') return;
    const n = parseNumber(t, dec);
    if (n == null) { if (allowEmpty) report(null); return; }
    // Report while typing, but never above max (a percentage box must not reach 430 on the way to 43.0).
    report(round(max != null ? Math.min(max, n) : n, dec));
  };
  const stepBy = (dir: 1 | -1, e: KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const base = parseNumber(draft, dec) ?? value ?? min ?? 0;
    const n = clamp(base + dir * step * (e.shiftKey ? 10 : 1));
    setDraft(editText(n)); if (commit === 'change') report(n);
  };

  return (
    <span className={boxClassName ?? 'relative flex items-center'}>
      <input
        {...rest}
        type="text"
        inputMode={dec ? 'decimal' : 'numeric'}
        autoComplete="off"
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        value={focused ? draft : shown(value)}
        onFocus={(e) => { setFocused(true); setDraft(value == null ? '' : editText(round(value, dec))); last.current = value ?? null; onFocus?.(e); }}
        onChange={(e) => typed(e.target.value)}
        onBlur={(e) => { finish(); setFocused(false); onBlur?.(e); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
          else if (e.key === 'Escape') { setDraft(last.current == null ? '' : editText(last.current)); if (commit === 'change' && last.current !== (value ?? null)) onChange(last.current); }
          else if (!readOnly && e.key === 'ArrowUp') stepBy(1, e);
          else if (!readOnly && e.key === 'ArrowDown') stepBy(-1, e);
          onKeyDown?.(e);
        }}
        className={`${className ?? ''}${suffix ? ' pr-8' : ''}`}
      />
      {suffix && <span className="pointer-events-none absolute right-2.5 text-[12px] text-[#667085]">{suffix}</span>}
    </span>
  );
}
